import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatDate, formatDateTime, STATUSES, STATUS_META, DONE_STATUSES, CONFIG } from "@/lib/api";
import { JobItemsList } from "@/components/OrderItems";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, textOn } from "@/components/StatusBadge";
import { QcChecklist, qcComplete } from "@/components/QcChecklist";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { scanFeedback } from "@/lib/feedback";
import { ExternalLink, LogIn, Printer, Loader2, ArrowRight, CheckCircle2, AlertTriangle, ScanLine, PauseCircle } from "lucide-react";

// Roles whose scan auto-advances the job; ordered list = scan sequence (editable in System Settings → Scan Rules)
export const scanFlow = (role) => CONFIG.scan_rules?.[role] || null;

export const scanTarget = (role, status) => {
  const flow = scanFlow(role);
  if (!flow || DONE_STATUSES.includes(status)) return null;
  const i = flow.indexOf(status);
  if (i === -1) return flow[0];
  return flow[i + 1] || null;
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-border/60 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v || "-"}</span></div>
);

const RemarkBox = ({ job, onUpdated }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (hold) => {
    if (!text.trim() && !hold) return toast.error("Enter a remark first");
    setBusy(true);
    try {
      if (hold) await api.patch(`/jobs/${job.id}`, { status: "On Hold", handover_note: text.trim() || "Problem reported from scan" });
      else await api.post(`/jobs/${job.id}/notes`, { text: text.trim(), visibility: "internal" });
      scanFeedback(hold ? "warn" : "success");
      toast.success(hold ? "Job put On Hold" : "Remark saved");
      setText(""); onUpdated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 bg-white text-slate-900 space-y-2" data-testid="scan-remark-box">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Remark / Problem</div>
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Note a problem or update for this job (optional)" data-testid="scan-remark-input" />
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={() => save(false)} disabled={busy} variant="outline" className="h-11" data-testid="scan-remark-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Remark"}</Button>
        <Button onClick={() => save(true)} disabled={busy || job.status === "On Hold"} className="h-11 bg-rose-600 hover:bg-rose-700 text-white" data-testid="scan-on-hold"><PauseCircle className="w-4 h-4 mr-1" /> Put On Hold</Button>
      </div>
    </Card>
  );
};

const AutoScanResult = ({ job, result }) => {
  const nextTarget = scanTarget(result.role, job.status);
  return (
    <Card className={`p-4 space-y-2 ${result.ok ? "bg-emerald-600 text-white" : "bg-amber-400 text-slate-950"}`} data-testid="scan-auto-result">
      <div className="flex items-center gap-2 text-lg font-bold">
        {result.ok ? <CheckCircle2 className="w-6 h-6" /> : <ScanLine className="w-6 h-6" />}
        {result.ok ? `Updated → ${result.to}` : result.msg}
      </div>
      <div className="text-sm opacity-90">{result.ok ? `PIC recorded: ${result.by}` : `Current status: ${job.status}`}</div>
      <div className="text-xs font-mono uppercase tracking-widest opacity-80 pt-1">
        {nextTarget ? `Next scan on this job → ${nextTarget}` : "Ready for next scan"}
      </div>
    </Card>
  );
};

const StatusUpdater = ({ job, onUpdated }) => {
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState("");
  const [qc, setQc] = useState(job.qc_checklist || {});
  const [busy, setBusy] = useState(false);
  const isQc = STATUS_META[job.status]?.dept === "qc";
  const idx = STATUSES.indexOf(job.status);
  const ordered = [...STATUSES.slice(idx + 1), ...STATUSES.slice(0, idx)].filter((s) => s !== job.status);

  const apply = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const body = { status: target, scan_mode: "single" };
      if (note.trim()) body.handover_note = note.trim();
      if (isQc) body.qc_checklist = qc;
      await api.patch(`/jobs/${job.id}`, body);
      scanFeedback("success");
      toast.success(`Status → ${target}`);
      setTarget(null); setNote("");
      onUpdated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 bg-white text-slate-900 space-y-3" data-testid="scan-status-updater">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Update status</div>
      {isQc && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">QC checklist (required before Delivery / Pick-Up)</div>
          <QcChecklist value={qc} onChange={setQc} compact />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {ordered.map((s) => {
          const c = STATUS_META[s]?.color || "#e2e8f0";
          const active = target === s;
          return (
            <button key={s} type="button" onClick={() => setTarget(active ? null : s)} data-testid={`scan-status-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`h-12 rounded-lg text-sm font-semibold border-2 transition-transform active:scale-95 ${active ? "ring-4 ring-cyan-400 scale-[1.02]" : ""}`}
              style={{ backgroundColor: c, color: textOn(c), borderColor: c === "#ffffff" ? "#94a3b8" : c }}>{s}</button>
          );
        })}
      </div>
      {target && (
        <div className="space-y-2 pt-1" data-testid="scan-confirm-box">
          <div className="flex items-center gap-2 text-sm"><StatusBadge status={job.status} /><ArrowRight className="w-4 h-4" /><StatusBadge status={target} /></div>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Handover note for the next department (optional)" data-testid="scan-handover-note" />
          <Button onClick={apply} disabled={busy || (isQc && STATUS_META[target]?.dept === "delivery" && !qcComplete(qc))} className="w-full h-12 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold" data-testid="scan-status-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm → ${target}`}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default function ScanJob() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const [autoResult, setAutoResult] = useState(null);
  const autoDone = useRef(null);
  const load = useCallback(() => api.get(`/public/scan/${id}`).then((r) => { setJob(r.data); return r.data; }).catch(() => setErr("Job not found")), [id]);
  useEffect(() => { setAutoResult(null); autoDone.current = null; load(); }, [load]);

  const staff = user && user.role !== "agent";
  const autoRole = staff && !!scanFlow(user.role);

  useEffect(() => {
    if (!job || !autoRole || autoDone.current === job.id) return;
    autoDone.current = job.id;
    const to = scanTarget(user.role, job.status);
    if (!to) { scanFeedback("warn"); setAutoResult({ ok: false, role: user.role, msg: DONE_STATUSES.includes(job.status) ? "Job already closed" : "Already at your stage — add remark below" }); return; }
    api.patch(`/jobs/${job.id}`, { status: to, scan_mode: "single" })
      .then(() => { scanFeedback("success"); setAutoResult({ ok: true, role: user.role, to, by: user.name }); toast.success(`${job.job_number} → ${to}`); load(); })
      .catch((e) => { scanFeedback("error"); const d = e?.response?.data?.detail; setAutoResult({ ok: false, role: user.role, msg: typeof d === "string" ? d : "Auto-update failed" }); });
  }, [job, autoRole, load, user?.name, user?.role]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="scan-error">{err}</div>;
  if (!job) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  const specs = Object.entries(job.specs || {});

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4" data-testid="scan-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3"><img src="/rozfa-logo-white.png" alt="Rozfa" className="h-8 w-auto" /><div className="text-xs font-mono text-slate-400 uppercase tracking-widest">{job.company || "Rozfa Printing"} • Job Scan</div></div>
          {staff && <div className="text-xs text-slate-400" data-testid="scan-user">{user.name}</div>}
        </div>
        {autoRole && (autoResult ? <AutoScanResult job={job} result={autoResult} /> : <Card className="p-4 bg-slate-800 text-slate-100 flex items-center gap-2" data-testid="scan-auto-pending"><Loader2 className="w-5 h-5 animate-spin" /> Updating status...</Card>)}
        <Card className="p-5 bg-white text-slate-900 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-3xl font-mono font-black" data-testid="scan-job-number">{job.job_number}</div>
              <div className="text-xs text-muted-foreground">Updated {formatDateTime(job.updated_at)}</div>
            </div>
            <StatusBadge status={job.status} />
          </div>
          <Row k="Customer" v={job.customer_name} />
          {job.agent_name && <Row k="Agent" v={job.agent_company || job.agent_name} />}
          <Row k="Product" v={`${job.product} × ${job.quantity}`} />
          {job.items?.length > 1 && <JobItemsList job={job} compact />}
          {!(job.items?.length > 1) && specs.map(([k, v]) => <Row key={k} k={k} v={v} />)}
          {!(job.items?.length > 1) && !specs.length && <><Row k="Material" v={job.material} /><Row k="Size" v={job.dimensions} /><Row k="Finishing" v={job.finishing} /></>}
          <Row k="Process Time" v={job.process_time} />
          <Row k="Due" v={formatDate(job.deadline)} />
          <Row k="Delivery" v={job.delivery_method} />
          <Row k="Current PIC" v={job.current_pic_name} />
          {job.label_remark && <div className="text-sm bg-yellow-50 border border-yellow-300 rounded p-2"><span className="font-semibold">Remark:</span> {job.label_remark}</div>}
          {job.notes && <div className="text-sm text-muted-foreground"><span className="font-semibold">Notes:</span> {job.notes}</div>}
        </Card>
        {staff ? (
          <>
            {autoRole ? <RemarkBox job={job} onUpdated={load} /> : <StatusUpdater job={job} onUpdated={load} />}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => nav(`/jobs/${job.id}`)} className="h-11 border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800" data-testid="scan-open-job"><ExternalLink className="w-4 h-4 mr-2" /> Full Job Card</Button>
              <Button variant="outline" onClick={() => nav(`/labels?ids=${job.id}`)} className="h-11 border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800" data-testid="scan-print-label"><Printer className="w-4 h-4 mr-2" /> Print Label</Button>
            </div>
            <Button onClick={() => nav("/scan")} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold" data-testid="scan-open-bulk"><ScanLine className="w-4 h-4 mr-2" /> Bulk Scan (camera)</Button>
          </>
        ) : (
          <Button onClick={() => nav(`/login?next=/scan/${job.id}`)} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold" data-testid="scan-open-job"><LogIn className="w-4 h-4 mr-2" /> Staff Login to Update Status</Button>
        )}
      </div>
    </div>
  );
}
